import { z } from 'zod';
import { SortDirection } from 'shared/src/domain/media-buy/media-buy-types';
import {
  AgDateFilter,
  AgNumberFilter,
  AgSetFilter,
  AgTextFilter
} from 'shared/src/domain/ag-grid-filter-types';
import { ZohoCampaignStatus } from 'shared/src/domain/zoho/zoho-types';
import { TeamUser, User } from 'shared/src/domain/user/user-types';
import { dateToUTC, dateToUTCOrNull } from 'shared/src/utils/zod/zod-utils';
import { ColumnVO } from '@ag-grid-community/core';

export const Account = z.object({
  id: z.string(),
  name: z.string(),
  referral_partner_name: z.string().nullish(),
  referral_commission: z.boolean().nullish(),
  referral_partner_comm: z.coerce.number().nullish()
});

export const Campaign = z.object({
  campaign_number: z.string(),
  campaign_id: z.string(),
  campaign_name: z.string(),
  stage: ZohoCampaignStatus,
  budget: z.coerce.number().nullable(),
  proposed_budget: z.coerce.number().nullable(),
  expected_revenue: z.coerce.number().nullable(),
  account_name: z.string(),
  account_type: z.string(),
  account_industry: z.string().nullable(),
  agency_name: z.string().nullable(),
  agency_gross_markup: z.coerce.number().nullable(),
  team: z.array(TeamUser).nullable(),
  team_name: z.string().nullable(),
  created_time: dateToUTCOrNull.nullable(),
  flight_date: dateToUTCOrNull.nullable(),
  end_date: dateToUTCOrNull.nullable(),
  goals_kpis: z.string().nullable(),
  lead_account_owner: z.string().nullable(),
  new_business: z.boolean().nullable(),
  account_manager: z.string().nullable(),
  senior_media_trader: z.string().nullable(),
  client_success_director: z.string().nullable(),
  region: z.string().nullable(),
  campaign_source: z.string().nullable(),
  media_traders: z.array(User)
});
export type Campaign = z.infer<typeof Campaign>;

export const CampaignSortColumn = z.union([
  z.literal('campaign_name'),
  z.literal('campaign_number'),
  z.literal('stage'),
  z.literal('budget'),
  z.literal('proposed_budget'),
  z.literal('expected_revenue'),
  z.literal('price'),
  z.literal('account_name'),
  z.literal('account_type'),
  z.literal('account_industry'),
  z.literal('agency_name'),
  z.literal('line_item_price_allocated'),
  z.literal('line_items_count'),
  z.literal('line_items_with_platforms_count'),
  z.literal('line_items_with_media_plans_count'),
  z.literal('total_line_items_media_budget'),
  z.literal('total_media_plans_count'),
  z.literal('total_media_plans_linked'),
  z.literal('percent_units_delivered'),
  z.literal('units_delivered'),
  z.literal('delivery_pacing'),
  z.literal('percent_budget_spend'),
  z.literal('media_spend'),
  z.literal('spend_pacing'),
  z.literal('percent_revenue_delivered'),
  z.literal('billable_revenue'),
  z.literal('active_media_plans'),
  z.literal('margin_actual'),
  z.literal('margin_variance'),
  z.literal('planned_margin'),
  z.literal('target_margin'),
  z.literal('target_margin_variance'),
  z.literal('flight_date'),
  z.literal('end_date'),
  z.literal('on_pace_revenue'),
  z.literal('revenue_remaining'),
  z.literal('on_pace_spend'),
  z.literal('budget_remaining'),
  z.literal('team_name'),
  z.literal('goals_kpis'),
  z.literal('new_business'),
  z.literal('lead_account_owner'),
  z.literal('senior_media_trader'),
  z.literal('client_success_director'),
  z.literal('account_manager'),
  z.literal('campaign_source'),
  z.literal('monthly_capped_actual_revenue'),
  z.literal('effective_monthly_capped_actual_revenue'),
  z.literal('lifetime_capped_actual_revenue'),
  z.literal('effective_lifetime_capped_actual_revenue'),
  z.literal('uncapped_revenue')
]);
export type CampaignSortColumn = z.infer<typeof CampaignSortColumn>;
export const campaignSortColumns = CampaignSortColumn.options.map(option => option.value);

export const CampaignSort = z.object({
  colId: CampaignSortColumn,
  sort: SortDirection
});
export type CampaignSort = z.infer<typeof CampaignSort>;

export const CampaignFilterTypes = z.object({
  name: AgTextFilter.optional(),
  campaign_number: AgTextFilter.optional(),
  campaign_id: AgTextFilter.optional(),
  campaign_source: AgTextFilter.optional(),
  region: AgTextFilter.optional(),
  account_name: AgTextFilter.optional(),
  account_type: AgSetFilter.optional(),
  account_industry: AgTextFilter.optional(),
  agency_name: AgTextFilter.optional(),
  stage: AgSetFilter.optional(),
  budget: AgNumberFilter.optional(),
  proposed_budget: AgNumberFilter.optional(),
  expected_revenue: AgNumberFilter.optional(),
  price: AgNumberFilter.optional(),
  effective_price: AgNumberFilter.optional(),
  flight_date: AgDateFilter.optional(),
  end_date: AgDateFilter.optional(),
  line_item_price_allocated: AgNumberFilter.optional(),
  line_items_count: AgNumberFilter.optional(),
  line_items_with_platforms_count: AgNumberFilter.optional(),
  line_items_with_media_plans_count: AgNumberFilter.optional(),
  total_media_plans_count: AgNumberFilter.optional(),
  total_media_plans_linked: AgNumberFilter.optional(),
  active_media_plans: AgNumberFilter.optional(),
  delivery_pacing: AgNumberFilter.optional(),
  percent_units_delivered: AgNumberFilter.optional(),
  units_delivered: AgNumberFilter.optional(),
  spend_pacing: AgNumberFilter.optional(),
  percent_budget_spend: AgNumberFilter.optional(),
  media_spend: AgNumberFilter.optional(),
  margin_actual: AgNumberFilter.optional(),
  margin_variance: AgNumberFilter.optional(),
  target_margin_variance: AgNumberFilter.optional(),
  target_margin: AgNumberFilter.optional(),
  planned_margin: AgNumberFilter.optional(),
  percent_revenue_delivered: AgNumberFilter.optional(),
  billable_revenue: AgNumberFilter.optional(),
  lead_account_owner: AgSetFilter.optional(),
  senior_media_trader: AgSetFilter.optional(),
  media_traders: AgSetFilter.optional(),
  client_success_director: AgSetFilter.optional(),
  account_manager: AgSetFilter.optional(),
  team_name: AgTextFilter.optional(),
  on_pace_revenue: AgNumberFilter.optional(),
  on_pace_spend: AgNumberFilter.optional(),
  budget_remaining: AgNumberFilter.optional(),
  revenue_remaining: AgNumberFilter.optional(),
  monthly_capped_actual_revenue: AgNumberFilter.optional(),
  effective_monthly_capped_actual_revenue: AgNumberFilter.optional(),
  lifetime_capped_actual_revenue: AgNumberFilter.optional(),
  effective_lifetime_capped_actual_revenue: AgNumberFilter.optional(),
  uncapped_revenue: AgNumberFilter.optional()
});
export type CampaignFilterTypes = z.infer<typeof CampaignFilterTypes>;

// Grouping support schemas
export const CampaignAggregateCols = z.object({
  budget: z.coerce.number(),
  price: z.coerce.number(),
  effective_price: z.coerce.number(),
  proposed_budget: z.coerce.number(),
  expected_revenue: z.coerce.number(),
  line_item_price_allocated: z.coerce.number(),
  line_item_effective_price_allocated: z.coerce.number(),
  line_items_count: z.coerce.number(),
  line_items_with_platforms_count: z.coerce.number(),
  line_items_with_media_plans_count: z.coerce.number(),
  active_media_plans: z.coerce.number(),
  total_line_items_media_budget: z.coerce.number(),
  units_delivered: z.coerce.number(),
  media_spend: z.coerce.number(),
  billable_revenue: z.coerce.number(),
  cost_of_goods_sold: z.coerce.number(),
  target_margin: z.coerce.number(),
  planned_margin: z.coerce.number(),
  margin_actual: z.coerce.number(),
  total_price_margin: z.coerce.number(),
  total_price_target_margin: z.coerce.number(),
  target_margin_price: z.coerce.number(),
  on_pace_revenue: z.coerce.number(),
  on_pace_spend: z.coerce.number(),
  delivery_pacing: z.coerce.number(),
  spend_pacing: z.coerce.number(),
  percent_budget_spend: z.coerce.number(),
  percent_revenue_delivered: z.coerce.number(),
  percent_units_delivered: z.coerce.number(),
  target_margin_variance: z.coerce.number(),
  margin_variance: z.coerce.number(),
  revenue_remaining: z.coerce.number(),
  budget_remaining: z.coerce.number(),
  total_media_plans_count: z.coerce.number(),
  total_media_plans_linked: z.coerce.number(),
  monthly_capped_actual_revenue: z.coerce.number(),
  effective_monthly_capped_actual_revenue: z.coerce.number(),
  lifetime_capped_actual_revenue: z.coerce.number(),
  effective_lifetime_capped_actual_revenue: z.coerce.number(),
  uncapped_revenue: z.coerce.number()
});

export const campaignAggregateColumnNames = Object.keys(
  CampaignAggregateCols.shape
) as (keyof z.infer<typeof CampaignAggregateCols>)[];

export const CampaignListRow = z
  .object({
    group: z.literal(false),
    group_key: z.string().optional(),
    count: z.number().optional()
  })
  .extend(Campaign.partial().shape)
  .extend(CampaignAggregateCols.shape);
export type CampaignListRow = z.infer<typeof CampaignListRow>;

export const CampaignAggregateRow = CampaignListRow.omit({
  group: true,
  group_key: true,
  count: true
}).extend({
  group: z.literal(true),
  group_key: z.string(),
  count: z.coerce.number()
});

export type CampaignAggregateRow = z.infer<typeof CampaignAggregateRow>;

export const CampaignTableRow = z.discriminatedUnion('group', [
  CampaignAggregateRow,
  CampaignListRow
]);
export type CampaignTableRow = z.infer<typeof CampaignTableRow>;

export const CampaignListRequest = z.object({
  page: z.coerce.number(),
  search: z.string().optional(),
  sort: CampaignSort.optional(),
  pageSize: z.coerce.number().optional(),
  filters: CampaignFilterTypes,
  groupKeys: z.string().array().optional(),
  rowGroupCols: z.custom<ColumnVO>().array().optional()
});

export type CampaignListRequest = z.infer<typeof CampaignListRequest>;

export const CampaignOverallResult = z.object({
  total: z.coerce.number(),
  overall_price: z.coerce.number().nullable(),
  overall_effective_price: z.coerce.number().nullable(),
  overall_line_item_price_allocated: z.coerce.number().nullable(),
  overall_line_item_effective_price_allocated: z.coerce.number().nullable(),
  overall_total_line_items_media_budget: z.coerce.number().nullable(),
  overall_delivery_pacing: z.coerce.number().nullable(),
  overall_billable_revenue: z.coerce.number().nullable(),
  overall_media_spend: z.coerce.number().nullable(),
  overall_percent_budget_spend: z.coerce.number().nullable(),
  overall_spend_pacing: z.coerce.number().nullable(),
  overall_percent_revenue_delivered: z.coerce.number().nullable(),
  overall_planned_margin: z.coerce.number().nullable(),
  overall_planned_margin_variance: z.coerce.number().nullable(),
  overall_target_margin: z.coerce.number().nullable(),
  overall_target_margin_variance: z.coerce.number().nullable(),
  overall_margin_actual: z.coerce.number().nullable(),
  overall_on_pace_revenue: z.coerce.number().nullable(),
  overall_revenue_remaining: z.coerce.number().nullable(),
  overall_on_pace_spend: z.coerce.number().nullable(),
  overall_budget_remaining: z.coerce.number().nullable(),
  overall_monthly_capped_actual_revenue: z.coerce.number().nullable(),
  overall_effective_monthly_capped_actual_revenue: z.coerce.number().nullable(),
  overall_lifetime_capped_actual_revenue: z.coerce.number().nullable(),
  overall_effective_lifetime_capped_actual_revenue: z.coerce.number().nullable(),
  overall_uncapped_revenue: z.coerce.number().nullable(),
  earliest_start_date: dateToUTC.nullable(),
  latest_end_date: dateToUTC.nullable()
});
export type CampaignOverallResult = z.infer<typeof CampaignOverallResult>;

export const CampaignListResult = z.object({
  campaigns: z.array(CampaignTableRow),
  overall: CampaignOverallResult.nullable()
});
export type CampaignListResult = z.infer<typeof CampaignListResult>;
